import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./Toast.css";
import { IconAlerta, IconFechar, IconSucesso } from "../../Shared/Icons/Icons";

const DURACAO_PADRAO_MS = 6000;

const ToastContext = createContext(null);

let proximoId = 0;

function ToastItem({ toast, duracao, onFechar }) {
  const [pausado, setPausado] = useState(false);

  // Pausa enquanto o usuario le (mouse em cima ou foco no botao de fechar).
  useEffect(() => {
    if (pausado) return undefined;
    const timer = setTimeout(() => onFechar(toast.id), duracao);
    return () => clearTimeout(timer);
  }, [pausado, toast.id, duracao, onFechar]);

  const ehErro = toast.variante === "erro";
  const Icone = ehErro ? IconAlerta : IconSucesso;

  return (
    <div
      className={`toast toast--${toast.variante}`}
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocus={() => setPausado(true)}
      onBlur={() => setPausado(false)}
    >
      <Icone className="toast-icon" />
      {/* O tipo nao pode depender so da cor/icone (WCAG 1.4.1). */}
      <span className="sr-only">{ehErro ? "Erro: " : "Sucesso: "}</span>
      <p className="toast-message">{toast.mensagem}</p>
      <button
        type="button"
        className="toast-close"
        aria-label="Fechar notificação"
        onClick={() => onFechar(toast.id)}
      >
        <IconFechar size={16} />
      </button>
    </div>
  );
}

export function ToastProvider({ children, duracao = DURACAO_PADRAO_MS }) {
  const [toasts, setToasts] = useState([]);

  const fechar = useCallback((id) => {
    setToasts((atuais) => atuais.filter((toast) => toast.id !== id));
  }, []);

  const mostrar = useCallback((mensagem, variante) => {
    proximoId += 1;
    const id = proximoId;
    setToasts((atuais) => [...atuais, { id, mensagem, variante }]);
    return id;
  }, []);

  const api = useMemo(
    () => ({
      sucesso: (mensagem) => mostrar(mensagem, "sucesso"),
      erro: (mensagem) => mostrar(mensagem, "erro"),
      fechar,
    }),
    [mostrar, fechar]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* A regiao fica sempre no DOM para o leitor de tela anunciar o que entrar nela. */}
      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            duracao={duracao}
            onFechar={fechar}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const contexto = useContext(ToastContext);
  if (!contexto) {
    throw new Error("useToast precisa estar dentro de <ToastProvider>.");
  }
  return contexto;
}
